import { countryCodeToCountryName } from "@/utils/countries.util";
import { downloadCsv } from "@/utils/file.util";
import { useMutation } from "@tanstack/react-query";
import { TFunction } from "i18next";

import useSalesyApiClient from "@/hooks/useSalesyApiClient";
import {
  CompanyListColumns,
  flatMapping,
  officeAddressColumns,
  officeHqAddressColumns,
} from "@/components/organisms/search/searchColumns";

export const listExportKey = ["export-list"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObject = { [key: string]: any };

type ExportListProps = {
  id: string;
  listName: string;
  visibleColumnIds: string[];
  t: TFunction;
};

export const useExportList = () => {
  const { apiClient } = useSalesyApiClient();

  const exportList = async (id: string) => {
    return apiClient.lists.export({ id });
  };

  const mutation = useMutation({
    mutationKey: listExportKey,
    mutationFn: exportList,
  });

  const { mutateAsync, ...mutationResult } = mutation;

  /**
   * Flattens a nested object by only including keys whose mapped column is present in the
   * columnIds array. The flattened key is returned as the key in the result (so that your
   * translation system can work with these flattened keys).
   *
   * For branches corresponding to "office.address" or "office_hq.address", if any of the
   * associated address columns are requested, then every field within that branch is included.
   *
   * @param columnIds Array of CompanyListColumns values to include in the flattened output.
   * @param mapping A record mapping flattened key paths (e.g. "info.name") to CompanyListColumns.
   * @param obj The object to flatten.
   * @param parentKey The prefix for keys (used during recursion).
   * @param result The accumulated flattened object.
   * @param forceInclude When true, all keys in the current branch will be included.
   * @returns A flattened object using the flattened key as the key.
   */
  const flattenObject = (
    columnIds: string[],
    mapping: Record<string, CompanyListColumns>,
    obj: AnyObject,
    parentKey: string = "",
    result: AnyObject = {},
    forceInclude: boolean = false,
  ): AnyObject => {
    for (const key in obj) {
      if (!Object.hasOwn(obj, key)) continue;

      if (
        key.toLowerCase() === "id" ||
        key.toLowerCase().startsWith("has_more_") ||
        key.toLowerCase().startsWith("logo") ||
        key.toLowerCase().startsWith("order") ||
        key.toLowerCase().startsWith("is_hq")
      ) {
        continue;
      }

      const value = obj[key];
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      // Determine if we should force-include all fields for address branches.
      let newForceInclude = forceInclude;
      if (!forceInclude && (newKey === "office.address" || newKey === "office_hq.address")) {
        if (newKey === "office.address" && officeAddressColumns.some((col) => columnIds.includes(col))) {
          newForceInclude = true;
        } else if (newKey === "office_hq.address" && officeHqAddressColumns.some((col) => columnIds.includes(col))) {
          newForceInclude = true;
        }
      }

      if (value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date)) {
        if (Object.hasOwn(value, "id") && Object.hasOwn(value, "value")) {
          result[newKey] = value.value;
          return result;
        }
        // Recursively flatten nested objects.
        flattenObject(columnIds, mapping, value, newKey, result, newForceInclude);

        // If office or office_hq address is requested, add the full address as a computed field.
        if (
          (newKey === "office.address" || newKey === "office_hq.address") &&
          (columnIds.includes("office_full_address") || columnIds.includes("hq_full_address")) &&
          value &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          const { street, line2, city, state, zipcode, country_code } = value;
          const countryName = countryCodeToCountryName(country_code);
          result[`${newKey}.full`] = [
            street,
            line2,
            city,
            state && zipcode ? `${state} ${zipcode}` : state,
            countryName,
          ]
            .filter((v) => v && v.trim())
            .join(", ");
        }

        // In a non-forced branch, if the object itself is mapped and requested, add it as JSON.
        if (!newForceInclude && mapping[newKey] && columnIds.includes(mapping[newKey])) {
          result[newKey] = JSON.stringify(value);
        }
      } else {
        if (newForceInclude) {
          // For a forced branch, include every primitive value regardless.
          result[newKey] = value === null || value === undefined ? "" : value;
        } else {
          // Normal behavior: include the value only if its mapping is desired.
          if (mapping[newKey] && columnIds.includes(mapping[newKey])) {
            result[newKey] = value === null || value === undefined ? "" : value;
          }
        }
      }
    }
    return result;
  };

  /**
   * Converts an array of objects to a CSV string.
   * - Excludes any fields named 'id'.
   * - Flattens nested objects with dot notation.
   * @param data The array of objects to convert.
   * @param t
   * @param columnIds
   * @returns A CSV-formatted string.
   */
  const jsonToCsv = (data: AnyObject[], t: TFunction, columnIds: string[]): string => {
    if (!data || data.length === 0) return "";

    // Flatten all objects and collect unique headers
    const flattenedData = data.map((item) => flattenObject(columnIds, flatMapping, item));
    const headersSet = new Set<string>();

    flattenedData.forEach((item) => {
      Object.keys(item).forEach((key) => headersSet.add(key));
    });

    const headers = Array.from(headersSet);

    // Create CSV header row
    const csvRows = [headers.map((header) => t(`search.table.${header}`)).join(",")];

    // Create CSV data rows
    flattenedData.forEach((item) => {
      const row = headers.map((header) => {
        const value = item[header];

        if (value === null || value === undefined || value === "") {
          return ``;
        } else if (typeof value === "string") {
          // Escape double quotes by replacing them with two double quotes
          return `"${value.replace(/"/g, '""')}"`;
        } else if (typeof value === "object") {
          // For arrays, join the values with a comma
          if (Array.isArray(value)) {
            return `"${value.join(", ")}"`;
          }

          // For objects, stringify them and escape double quotes
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        } else {
          // Convert other data types to string
          return String(value);
        }
      });

      csvRows.push(row.join(","));
    });

    // Combine all rows into a single CSV string
    return csvRows.join("\n");
  };

  const downloadExportedList = async ({ id, listName, visibleColumnIds, t }: ExportListProps) => {
    const allItems = await mutateAsync(id);
    const csv = jsonToCsv(allItems.items, t, visibleColumnIds);
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const formattedDate = `${year}${month}${day}`;

    downloadCsv(csv, `octrace-${listName}-${formattedDate}.csv`);
  };

  return {
    ...mutationResult,
    exportList: downloadExportedList,
  };
};
